import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from "@hello-pangea/dnd";
import styles from "./testPlanScenarios.module.scss";
import { Scenario } from "../../../types/scenarioTypes";
import { useTranslation } from "react-i18next";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../../store";
import dragIcon from '../../../shared/images/drag.svg';
import { ReactComponent as DeleteIcon } from "../../../shared/images/delete.svg";
import {
  useEffect,
  useMemo,
  useState
} from "react";
import { setUpdateModal } from "../../../store/slices/mainSlice";
import { ReactComponent as AddIcon } from "../../../shared/images/add.svg";
import {
  TestPlan,
  TestPlanScenario
} from "../../../types/testPlanTypes";
import { useUpdateTestPlanScenariosMutation } from "../../../api/testPlanService";
import { updateTestPlanSelectedScenarios } from "../../../store/slices/testPlanSlice";
import { AddScenariosToTestPlanModal } from "../../addScenariosToTestPlanModal/addScenariosToTestPlanModal";
import {
  getTemplateAndTestCaseByTestCaseId,
  getTransformTestCase
} from "../../../utils/testCaseUtils";
import {
  TestCaseTemplate,
  TransformTestCase
} from "../../../types/testCaseTemplateTypes";

type TestPlanScenariosProps = {
  testPlan: TestPlan;
  testCaseTemplates?: Array<TestCaseTemplate>
  refetchTestPlans: () => void;
  cancelChanges: () => void;
}

export const TestPlanScenarios = ({ testPlan, refetchTestPlans, testCaseTemplates, cancelChanges }: TestPlanScenariosProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation();

  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);
  const selectedTestPlan = useSelector((state: RootState) => state.testPlanSlice.selectedTestPlan);
  const selectedScenarios = useSelector((state: RootState) => state.testPlanSlice.selectedScenarios);

  const [showScenariosToTestPlanModal, setShowScenariosToTestPlanModal] = useState<boolean>(false)

  const [updateTestPlanScenarios, { isSuccess: isSuccessUpdateScenarioRequests }] = useUpdateTestPlanScenariosMutation();

  useEffect(() => {
    if (isSuccessUpdateScenarioRequests) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      refetchTestPlans();
    }
  }, [isSuccessUpdateScenarioRequests, dispatch, refetchTestPlans]);

  const dragEnded = (param: DropResult) => {
    const { source, destination } = param;
    if (!!destination) {
      const filteredTestPlanScenarios: TestPlanScenario[] = filteredScenarios.map(item => ({ ...item }));
      const spliceItem = filteredTestPlanScenarios.splice(source.index, 1)[0];
      filteredTestPlanScenarios.splice(destination.index, 0, spliceItem);
      filteredTestPlanScenarios.forEach((item, index) => {
        item.queueNumber = index;
        item.isUpdated = true;
      });

      const newSelectedScenarios = [...filteredTestPlanScenarios, ...selectedScenarios.filter(item => item.isDeleted)];
      dispatch(updateTestPlanSelectedScenarios(newSelectedScenarios));
    }
  }

  const saveScenario = (): void => {
    updateTestPlanScenarios({
      ...selectedTestPlan,
      assignedScenarios: selectedScenarios.map(item => ({
        scenarioId: item.id as number,
        isDeleted: item.isDeleted,
        queueNumber: item.queueNumber,
        isUpdated: item.isUpdated,
        id: !!item.internalId ? +item.internalId : undefined
      }))
    });
  }

  const deleteScenarioForTestPlan = (scenarioIndex: number): void => {
    const newSelectedScenario: Array<TestPlanScenario> = selectedScenarios.map(item => ({ ...item }));

    const scenario = newSelectedScenario.filter(item => !item.isDeleted)[scenarioIndex];
    if (!!scenario.internalId) {
      scenario.isDeleted = true;
      scenario.queueNumber = -1;
    } else {
      newSelectedScenario.splice(scenarioIndex, 1);
    }
    newSelectedScenario.filter(item => !item.isDeleted).forEach(((item, index) => {
      item.queueNumber = index;
      item.isUpdated = true;
    }));

    dispatch(updateTestPlanSelectedScenarios(newSelectedScenario));
  }

  const cancelChangesTestPlan = (): void => {
    cancelChanges();
  }

  const clickAddScenarioToTestPlan = (): void => {
    setShowScenariosToTestPlanModal(true);
  }

  const closeAddScenarioToTestPlan = (isSave: boolean, scenarios?: Array<Scenario>): void => {
    if (isSave) {
      const newSelectedScenarios = [...selectedScenarios];
      scenarios?.forEach(scenario => {
        const newTestCases: Array<TransformTestCase> = [];
        scenario.assignedRequests.forEach(assignedRequest => {
          const testCaseAndTemplate = getTemplateAndTestCaseByTestCaseId(testCaseTemplates || [], assignedRequest.id as number);
          if (testCaseAndTemplate) {
            newTestCases.push(getTransformTestCase(selectedVariables, testCaseAndTemplate.template, testCaseAndTemplate.testCase));
          }});
        newSelectedScenarios.push({
          ...scenario,
          testCases: newTestCases,
          queueNumber: newSelectedScenarios.filter(item => !item.isDeleted).length
        });
      });
      dispatch(updateTestPlanSelectedScenarios(newSelectedScenarios));
    }
    setShowScenariosToTestPlanModal(false);
  }

  const filteredScenarios: Array<TestPlanScenario> = useMemo(() => {
    return selectedScenarios.filter(item => !item.isDeleted);
  }, [selectedScenarios]);

  const showSaveCancelTestPlan = useMemo(() => {
    return selectedScenarios.some(item => item.isDeleted || item.isUpdated || !item.internalId);
  }, [selectedScenarios]);

  return (
    <>
      <DragDropContext onDragEnd={dragEnded}>
        <Droppable droppableId={`${testPlan.id}`}>
          {(provided) => (
            <div className={styles.container} ref={provided.innerRef}>
              <div className={styles.scenarios}>
                {
                  filteredScenarios.map((scenario, index) => (
                    <Draggable draggableId={`${scenario?.internalId}-${index}`} index={index} key={`${scenario?.internalId}-${index}`}>
                      {(_provided, snapshot) => (
                        <div className={`${styles.scenario} ${snapshot.isDragging ? styles.hovering : ""}`}
                             key={index}
                             {..._provided.draggableProps}
                             ref={_provided.innerRef}>
                          <div className={styles.info}>
                            <img src={dragIcon} alt="" {..._provided.dragHandleProps}/>
                            <span className={styles.name}>{scenario?.name}</span>
                          </div>
                          <DeleteIcon onClick={() => deleteScenarioForTestPlan(index)}/>
                        </div>
                      )}
                    </Draggable>
                  ))
                }
                {provided.placeholder}
              </div>
              <div className={styles.bottomContainer}>
                <div className={styles.save}>
                  { showSaveCancelTestPlan &&
                    <>
                      <span onClick={saveScenario}>{t("common.save")}</span>
                      <span onClick={cancelChangesTestPlan}>{t("common.cancel")}</span>
                    </>
                  }
                </div>
                <div className={styles.addScenario} onClick={() => clickAddScenarioToTestPlan()}>
                  <AddIcon/>
                  {t("scenario.add")}
                </div>
              </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {showScenariosToTestPlanModal && (<AddScenariosToTestPlanModal onClose={closeAddScenarioToTestPlan}
                                                                     testPlanName={testPlan.name} />)}
    </>

  );
}